/**
 * Permission helpers for the admin-side WeChat login & binding management
 * section in `/settings` (Issue #269).
 *
 * Mirrors the backend `RequirePermission` decorators on
 * `apps/api/src/modules/admin-customer-wechat/controllers/admin-customer-wechat.controller.ts`.
 *
 * Red lines:
 *   - The "read" tier covers list / detail of bindings, work orders, and the
 *     login policy. A customer-service role with read-only access must never
 *     see write / approve controls in the UI.
 *   - The "write" tier covers creating / submitting / cancelling work
 *     orders. It does NOT include approval — that requires the dedicated
 *     `approve` permission so a single CSR cannot self-approve.
 *   - The "approve" tier covers approve / reject / execute. Backend
 *     additionally refuses approval/execute by the original requester.
 *   - The "policy" tier covers the per-tenant/org login switch.
 */

export const WECHAT_BINDING_READ_PERMISSION = 'wechat_bindings:read';
export const WECHAT_BINDING_WORK_ORDER_WRITE_PERMISSION =
  'wechat_binding_work_orders:write';
export const WECHAT_BINDING_WORK_ORDER_APPROVE_PERMISSION =
  'wechat_binding_work_orders:approve';
export const WECHAT_LOGIN_POLICY_WRITE_PERMISSION = 'wechat_login_policy:write';

const SUPER_ADMIN = '*:*';

function hasAny(permissions: readonly string[], needles: readonly string[]): boolean {
  if (permissions.includes(SUPER_ADMIN)) return true;
  for (const needle of needles) {
    if (permissions.includes(needle)) return true;
  }
  return false;
}

export function canReadWechatBindingManagement(
  permissions: readonly string[],
): boolean {
  return hasAny(permissions, [
    WECHAT_BINDING_READ_PERMISSION,
    WECHAT_BINDING_WORK_ORDER_WRITE_PERMISSION,
    WECHAT_BINDING_WORK_ORDER_APPROVE_PERMISSION,
    WECHAT_LOGIN_POLICY_WRITE_PERMISSION,
  ]);
}

export function canWriteWechatBindingWorkOrders(
  permissions: readonly string[],
): boolean {
  return hasAny(permissions, [WECHAT_BINDING_WORK_ORDER_WRITE_PERMISSION]);
}

export function canApproveWechatBindingWorkOrders(
  permissions: readonly string[],
): boolean {
  return hasAny(permissions, [WECHAT_BINDING_WORK_ORDER_APPROVE_PERMISSION]);
}

export function canWriteWechatLoginPolicy(
  permissions: readonly string[],
): boolean {
  return hasAny(permissions, [WECHAT_LOGIN_POLICY_WRITE_PERMISSION]);
}
